// Shared async-state primitives — every feature page MUST render one of these for
// its loading / empty / error states (FACTS PACK §LOCKED). Keeps states visually
// consistent across the console. 403 gets a dedicated "無權限" message because the
// server can reject any admin call even with a valid session.

import type { ReactNode } from 'react';
import { ApiError } from '../lib/api';

export function LoadingState({ label = '載入中…' }: { label?: string }) {
  return (
    <div className="state-block">
      <div className="spinner" />
      <div className="text-secondary">{label}</div>
    </div>
  );
}

export function EmptyState({
  title = '沒有資料',
  detail,
  action,
  icon,
}: {
  title?: string;
  detail?: ReactNode;
  action?: ReactNode;
  /** 可選圖示(inline SVG 或 emoji);置於標題上方,填補整頁空狀態的視覺分量。 */
  icon?: ReactNode;
}) {
  return (
    <div className="state-block">
      {icon && <div className="state-icon">{icon}</div>}
      <div className="state-title">{title}</div>
      {detail && <div className="state-detail">{detail}</div>}
      {action}
    </div>
  );
}

/** Renders an error consistently; special-cases 403 (server RBAC) and shows request_id for support. */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const isApi = error instanceof ApiError;
  const forbidden = isApi && error.status === 403;
  const title = forbidden ? '無權限' : '載入失敗';
  const message = forbidden
    ? '伺服器拒絕了這項操作（需要更高權限）。'
    : isApi
      ? error.message
      : '發生未預期的錯誤，請稍後再試。';
  const requestId = isApi ? error.requestId : null;
  return (
    <div className="state-block">
      <div className="state-title">{title}</div>
      <div className="state-detail">{message}</div>
      {requestId && (
        <div className="text-tertiary mono" style={{ fontSize: 'var(--font-size-xs)' }}>
          request_id: {requestId}
        </div>
      )}
      {onRetry && !forbidden && (
        <button className="btn-ghost btn-sm" onClick={onRetry}>
          重試
        </button>
      )}
    </div>
  );
}
